<template>
  <div>
    <!--
      Compact calendar shell.
      The wrapper has an explicit viewport-based height so FullCalendar can
      use height: '100%' and fit without scrolling. dayMaxEvents collapses
      overflow into a "+N more" popover so cells stay short instead of
      growing the row.
    -->
    <div class="calendar calendar--compact">
      <div  style="position: absolute; margin-top: 6px; right: 16px; z-index: 2;">
        <b-spinner small v-if="loading">{{$t('operations.loading')}}</b-spinner>
        <b-link v-else><i class="fa-solid fa-rotate-right" @click="refreshCalendar"></i></b-link>
      </div>

      <FullCalendar  ref="calendar" v-bind:options="calendarOptions"/>
    </div>

    <!--
      Event-details modal (brand new UI; replaces the per-event b-popover loop).
      All logic preserved:
        - opened by onEventClick(eventInfo) which sets selectedEvent / selectedEventIndex
        - dismiss / edit / close use the same handlers as before
        - body still rendered by <calendar-event-basic> so the inner data flow
          (clientSelect, updated, accept/reject) keeps working as-is
    -->
    <b-modal v-model="eventModal.show"
             :title="null"
             size="lg"
             centered
             hide-header
             hide-footer
             modal-class="cal-event-modal"
             body-class="p-0"
             content-class="cal-event-modal__content"
             no-close-on-backdrop
    >
      <template v-if="selectedEvent">
        <!-- Hero header: gradient strip + title + type/status pills -->
        <div class="cal-event-modal__hero"
             :class="`cal-event-modal__hero--${heroVariantForEvent(selectedEvent)}`">
          <div class="d-flex align-items-start justify-content-between">
            <div class="cal-event-modal__title">
              <div class="cal-event-modal__date">
                <i class="fa-regular fa-calendar mr-1"></i>
                <span>{{ formatRange(selectedEvent.start, selectedEvent.end) }}</span>
              </div>
              <h3 class="mb-0">{{ selectedEvent.title || $t('calendar.details') }}</h3>
              <div class="cal-event-modal__pills mt-2">
                <span v-if="selectedEvent.type"
                      class="cal-event-modal__pill"
                      :class="`cal-event-modal__pill--${$t('calendarTypes.' + selectedEvent.type + '.color')}`">
                  {{ $t(`calendarTypes.${selectedEvent.type}.name`) }}
                </span>
                <span v-if="selectedEvent.status === 'CANCELLED'"
                      class="cal-event-modal__pill cal-event-modal__pill--secondary">
                  <i class="fa-solid fa-ban mr-1"></i>{{ $t('calendar.cancelled') }}
                </span>
              </div>
            </div>
            <b-button variant="flat-light" size="sm" pill class="cal-event-modal__close"
                      @click="closeEventModal" :title="$t('operations.close')">
              <feather-icon icon="XIcon" size="18"/>
            </b-button>
          </div>
        </div>

        <!-- Body: existing detail component, just in a nicer frame -->
        <div class="cal-event-modal__body">
          <calendar-event-basic v-bind:event="selectedEvent"
                                @clientSelect="onClientSelect"
                                @updated="ev=>updateEvent(selectedEventIndex, ev)"/>
        </div>

        <!-- Footer: action bar — same handlers the popover used -->
        <div class="cal-event-modal__footer">
          <b-button v-if="selectedEvent.userId === myId"
                    variant="outline-danger" size="sm"
                    @click="dismissEvent(selectedEventIndex, selectedEvent.id)">
            <feather-icon icon="ArchiveIcon" class="mr-1"/>{{ $t('operations.dismiss') }}
          </b-button>
          <b-button variant="outline-primary" size="sm"
                    @click="editEvent(selectedEvent.id)">
            <feather-icon icon="Edit3Icon" class="mr-1"/>{{ $t('operations.edit') }}
          </b-button>
          <b-button variant="primary" size="sm" @click="closeEventModal">
            {{ $t('operations.close') }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <calendar-event-modal ref="eventModal" @submitted="refreshCalendar"/>
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import allLocales from '@fullcalendar/core/locales-all'
import listPlugin from '@fullcalendar/list'
import { mapActions, mapGetters } from 'vuex'
import calendarEventBasic from '@/components/partial/calendar/calendarEventBasic'
import mixinBase from '@/components/mixin/mixinBase'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinTable from '@/components/mixin/mixinTable'
import CalendarEventModal from '@/components/modal/calendarEventModal'
import { CalendarStatusEnum } from '@/model/calendar/CalendarStatusEnum'
import mixinPopover from '@/components/mixin/mixinPopover'
export default {
  name: 'Calendar',
  mixins: [mixinBase, mixinPopover, mixinNotifications, mixinTable],
  components: {
    CalendarEventModal,
    FullCalendar,
    calendarEventBasic
  },
  data() {
    return {
      calendarOptions: {
        fixedWeekCount: false,
        // Fit to the wrapper (which is sized to the viewport in CSS below)
        // instead of growing tall enough to need page scroll.
        height: '100%',
        expandRows: true,
        // Show ALL events in a day — no "+N more" link/popover. When a day
        // has more events than fit, the cell scrolls internally (see the
        // .fc-daygrid-day-events overflow rule in the styles below) instead
        // of opening a popover or stretching the whole row.
        dayMaxEvents: false,
        // Slightly stretched cells (wider than tall) — looks less square
        // and gives more horizontal room for event titles.
        aspectRatio: 1.8,
        locales: allLocales,
        locale: this.$t('lang.base') || 'en',//this.currentLang, // the initial locale
        plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
        headerToolbar: {
          // start: 'sidebarToggle, prev,next, title',
          start: 'prev,next,title',
          end: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth',
        },
        initialView: 'dayGridMonth',
        //dateClick: this.onDateClick,
        editable: true,
        eventDrop: this.moveEvent,
        events: this.onCalendarRefresh,
        eventClick: this.onEventClick,
        eventDidMount: this.onEventDidMount,
        slotLabelFormat: {
          hour: '2-digit',
          minute: '2-digit',
          omitZeroMinute: false,
          hour12: false
        },
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
          omitZeroMinute: false,
          hour12: false
        }

      },

      events: [],
      cal: { start: '', end: '' },
      loadedExternalEvents: {},
      dialogs: {
        popovers: false
      },
      // Event-details modal state. selectedEventIndex is the index into
      // this.events so updateEvent / dismissEvent keep working with the
      // same (index, ev) signatures they used under the popover flow.
      eventModal: { show: false },
      selectedEvent: null,
      selectedEventIndex: -1,
      listeners:{
        refresh(){}
      }
    }
  },
  props:{
    types:Array,
    users:Array,
  },
  watch: {
    types:{
      handler() {
        this.refreshCalendar()
      },
      // immediate: true
    },
    users:{
      handler() {
        this.refreshCalendar()
      },
      // immediate: true
    }
  },
  created() {
    console.log('calendar created')
    this.calendarOptions.locale = 'en'
    this.$root.$on('crm::calendar::refresh',this.refreshCalendar)
  },
  beforeDestroy() {
    this.$root.$off('crm::calendar::refresh',this.refreshCalendar)
  },
  computed: {
    ...mapGetters('user', ['myId']),
  },
  methods: {
    ...mapActions('calendar', ['getExternalCalendars', 'getCalendars', 'cancelEvent']),
    refreshCalendar() {
      console.log('refreshCalendar')
      let calendarApi = this.$refs.calendar.getApi()
      calendarApi.refetchEvents()
    },
    onCalendarRefresh(info, successCallback, failureCallback) {
      console.log('onCalendarRefresh')
      this.cal.start = this.$moment(info.start).startOf('month')
          .format('YYYY-MM-DD')
      this.cal.end = this.$moment(info.end).endOf('month')
          .format('YYYY-MM-DD')
      this.refresh()
          .then(() => successCallback(this.adaptEvents()), failureCallback)
    },
    refresh() {
      let $this = this
      let token = this.setLoading();
      return new Promise((resolve, reject) => {
        this.getCalendars({
          from: $this.cal.start,
          until: $this.cal.end,
          userid:-1,
          repList: this.users
        })
            .then(
                (events) => {
                  $this.updateEvents(events)
                  resolve()
                },
                reject
            )
      }).finally(() => {
        this.setLoaded(token)
      })
    },
    updateEvents(apiEvents) {
      if (!Array.isArray(apiEvents)) {
        apiEvents = []
      }
      this.events=apiEvents.map(e=>({...e, external : false}));
      return this.events
    },
    adaptEvents() {
      let events =  this.events.filter(this.eventsFilter).map(ev => ({
        start: this.$moment.serverDateTime(ev.start).toServerDateTimeFormat(),
        end:  this.$moment.serverDateTime(ev.end).toServerDateTimeFormat(),
        title: (ev.status === CalendarStatusEnum.CANCELLED ? `[${this.$t('calendar.cancelled')}] ` : '') + this.eventTitleForPreview(ev),
        id: 'ev-' + ev.id,
        rawId: ev.id,
        classNames:[('bg-light-'+this.$t(`calendarTypes.${ev.type}.color`)), `fc-dot-${this.badgeVariant(ev)}`, (ev.status === CalendarStatusEnum.CANCELLED ? 'fc-event-cancelled' : '')],
        draggable: (ev.userid === this.myId),
        editable: (ev.userid === this.myId),
        overlap: (ev.userid === this.myId),
      }))
      console.log("adaptEvents", events);
      return events;
    },
    badgeVariant(event){
      if(event == null || event.userId === this.myId){
        return 'primary';
      }
      let userData = event.users.find(u=>u.userId === this.myId)
      if(userData == null || userData.status == null || userData.status === CalendarStatusEnum.PENDING){
        return 'dark';
      }
      if(userData.status === CalendarStatusEnum.APPROVED){
        return 'success';
      }
      if(userData.status === CalendarStatusEnum.REJECTED){
        return 'danger';
      }
      return 'dark';
    },
    eventTitleForPreview(event){
      if (event.clientsIds==null || event.clientsIds.length === 0 || event.clientNames == null) {
        return event.title;
      }

      let clientId = event.clientsIds[0];
      return event.clientNames[clientId];

    },
    eventsFilter(ev) {
      if(!Array.isArray(this.types)){
        return true;
      }
      if(!this.types.includes('cancelled') && ev.status === CalendarStatusEnum.CANCELLED){
        return false;
      }
      if(!this.types.includes('rejected') && this.badgeVariant(ev) === 'danger'){
        return false;
      }
      if(!this.types.includes('approved') && this.badgeVariant(ev) === 'success'){
        return false;
      }
      if(!this.types.includes('pending') && this.badgeVariant(ev) === 'dark'){
        return false;
      }
      return this.types.includes(ev.type);
    },
    onEventDidMount: function (eventInfo) {
      let $this = this;
      eventInfo.el.id = eventInfo.event.id

      eventInfo.el.ondblclick = function (){
        $this.editEvent(eventInfo.event.extendedProps.rawId)
      }
      this.refreshPopovers()
    },
    refreshPopovers() {
      let $this = this
      //Hack in order to reload the popovers
      this.dialogs.popovers = false
      setTimeout(() => {
        this.dialogs.popovers = true
        $this.$forceUpdate()
      }, 1000)
    },

    onDateClick(arg) {
      this.$refs['eventModal'].showModal(null, arg.dateStr);
    },
    moveEvent(info) {
      let eventInfo = info.event
      if (!confirm('Are you sure about this change?')) {
        info.revert()
        return
      }
      let event = this.events.find(e => e.id === parseInt(eventInfo.id.substr(3)))
      if (event == null) {
        info.revert()
        return
      }
      event = { ...event }
      event.start = this.$moment(eventInfo.start)
          .format('YYYY-MM-DD HH:mm')
      event.end = this.$moment(eventInfo.end)
          .format('YYYY-MM-DD HH:mm')
      this.update({
        id: event.id,
        event: event
      })
      this.refreshPopovers()
    },
    editEvent(id) {
      // Close the details modal first so the edit modal doesn't stack on
      // top of it (visually messy, and prevents focus-trap fights).
      this.closeEventModal()
      this.$refs['eventModal'].showModal(id);
      this.closePopover()
    },
    onEventClick(eventInfo) {
      // Resolve the clicked FC event back to the raw event in this.events
      // via rawId (set in adaptEvents). We keep the index around so the
      // existing updateEvent(index, ev) / dismissEvent(index, id) signatures
      // continue to work unchanged.
      const rawId = eventInfo.event.extendedProps && eventInfo.event.extendedProps.rawId
      const idx = this.events.findIndex(e => e.id === rawId)
      if (idx < 0) {
        // Fallback: fire the legacy popover signal in case anything still
        // listens. Should never hit in practice.
        this.$root.$emit('bv::show::popover', 'ev' + eventInfo.event.id)
        return
      }
      this.selectedEventIndex = idx
      this.selectedEvent = this.events[idx]
      this.eventModal.show = true
    },
    closeEventModal() {
      this.eventModal.show = false
      this.selectedEvent = null
      this.selectedEventIndex = -1
    },
    // Legacy helper — popover loop is gone but several call-sites (and
    // potentially external mixins) still invoke this. Kept as a no-op that
    // also closes the new modal so the perceived behavior is the same.
    closePopover(popover) {
      this.closeEventModal()
      if (popover != null && typeof popover === 'string') {
        this.$root.$emit('bv::hide::popover', popover)
      } else {
        this.$root.$emit('bv::hide::popover')
      }
    },
    // Maps an event to a hero-strip variant for the modal header. Reuses
    // the same color tokens the badges already use (primary/success/danger/
    // dark) so the modal feels consistent with the calendar cell.
    heroVariantForEvent(ev) {
      const v = this.badgeVariant(ev)
      return v || 'primary'
    },
    // Compact human-readable range for the modal hero. Same datetime
    // format the rest of the app uses (via the momentDateTime filter and
    // $moment helper installed app-wide).
    formatRange(start, end) {
      if (!start) return ''
      const s = this.$moment(start)
      const e = end ? this.$moment(end) : null
      if (!e || !e.isValid()) return s.format('ddd D MMM YYYY · HH:mm')
      if (s.isSame(e, 'day')) {
        return s.format('ddd D MMM YYYY · HH:mm') + ' – ' + e.format('HH:mm')
      }
      return s.format('D MMM · HH:mm') + ' → ' + e.format('D MMM · HH:mm')
    },
    onClientSelect(id) {
      console.log('calendar-> onClientSelect(' + id + ')')
      this.$root.$emit('crm::client::select', id)
    },
    async dismissEvent(index, id) {
      // Close the details modal so the confirm dialog isn't competing with
      // it for focus/backdrop. The result toast still fires on completion.
      this.closeEventModal()
      this.closeAllPopovers()
      let result = await this.$bvModal.msgBoxConfirm(this.$t('calendar.action.dismissMsg', {event:this.events[index].title}), {
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'YES',
        cancelTitle: 'NO',
        hideHeaderClose: false,
        centered: true
      });
      if(!result){
        return
      }
      let $this = this
      this.cancelEvent(id)
          .then(
              ev => {
                $this.updateEvent(index, ev)
              },
              err => $this.showErrorNotification($this.$t('globalMessages.updateDataError', { system: $this.$t('menu.clients') }), err.error)
          )
    },
    updateEvent(index, ev) {
      let event = this.events[index];
      if(event.id === ev.id){
        this.events[index] = ev;
        this.refreshPopovers();
        this.refreshCalendar();
      }
    },
  }
}
</script>

<style lang="scss">

.dark-layout{
  // Loud today highlight in dark mode — bright indigo border, tinted
  // background, and a glowing day-number pill. Mirrors the light-mode
  // treatment with adjusted contrast for the dark canvas.
  .fc .fc-day-today {
    background-color: rgba(0, 123, 255, 0.22) !important;
    box-shadow: inset 0 0 0 2px #007BFF !important;
  }
  .fc .fc-day-today .fc-daygrid-day-top::after {
    content: 'TODAY';
    position: absolute;
    top: 4px;
    right: 6px;
    font-size: 0.6rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    color: #ffffff;
    background: #007BFF;
    padding: 1px 5px;
    border-radius: 4px;
    z-index: 1;
    pointer-events: none;
  }
  .fc .fc-day-today .fc-daygrid-day-number {
    background-color: #007BFF !important;
    color: #ffffff !important;
    border-radius: 999px;
    font-weight: 700;
    box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.35),
                0 2px 6px rgba(0, 123, 255, 0.55);
  }
  .fc .fc-col-header-cell.fc-day-today {
    background-color: #007BFF !important;
  }
  .fc .fc-col-header-cell.fc-day-today .fc-col-header-cell-cushion {
    color: #ffffff !important;
    font-weight: 800;
  }

  .fc .fc-list .fc-list-day-cushion {
    background-color: rgba(27, 35, 55, 0.82) !important;
  }

  // Dark-mode polish for the redesigned toolbar / cells / events.
  // Mirrors the light-mode tints but with adjusted opacities so they read
  // on the navy background.
  .calendar.calendar--compact {
    .fc .fc-scrollgrid,
    .fc .fc-scrollgrid td,
    .fc .fc-scrollgrid th,
    .fc .fc-theme-standard td,
    .fc .fc-theme-standard th {
      border-color: rgba(255, 255, 255, 0.08);
    }
    .fc .fc-col-header-cell {
      background-color: rgba(0, 123, 255, 0.10);
    }
    .fc .fc-col-header-cell-cushion {
      color: #d0d2d6;
    }
    .fc .fc-daygrid-day-number {
      color: #d0d2d6;
    }
    .fc .fc-day-sat,
    .fc .fc-day-sun {
      background-color: rgba(255, 255, 255, 0.02);
    }
    .fc .fc-day-other {
      background-color: rgba(255, 255, 255, 0.03);
    }
    .fc .fc-button-group {
      background: rgba(255, 255, 255, 0.05);
    }
    .fc .fc-button-group > .fc-button {
      color: #b4b7bd;
    }
    .fc .fc-button-group > .fc-button.fc-button-active {
      background: #283046;
      color: #ffffff;
    }
    .fc .fc-prev-button,
    .fc .fc-next-button {
      color: #b4b7bd;
    }
    .fc-daygrid-event {
      box-shadow: 0 1px 2px rgba(0,0,0,0.30);
    }
    .fc-daygrid-event:hover {
      box-shadow: 0 3px 8px rgba(0,0,0,0.45);
    }
  }
}

// Viewport-fitted compact calendar. Height is computed to leave room for
// the app navbar, the card padding around it, and a small breathing
// margin — so the calendar fills the screen without ever scrolling the
// page. dayMaxEvents (set in JS) keeps cell heights from blowing up.
.calendar.calendar--compact {
  height: calc(100vh - 180px);
  min-height: 480px;
  position: relative;

  // ----- Overall canvas -----
  // Rounded card-like container, subtle outer border, soft hover shadow.
  // Picks up the surrounding b-card's background so dark mode just works.
  .fc {
    border-radius: 12px;
    overflow: hidden;
    background: transparent;
    font-family: inherit;
  }
  // The outermost FullCalendar table border is harsh by default — soften
  // it to a hairline and tint with the theme grey.
  .fc .fc-scrollgrid,
  .fc .fc-scrollgrid td,
  .fc .fc-scrollgrid th,
  .fc .fc-theme-standard td,
  .fc .fc-theme-standard th {
    border-color: rgba(34, 41, 47, 0.08);
  }

  // ----- Header toolbar -----
  // Polished header: bigger title, segmented pill button group for the
  // view switcher, ghost-style prev/next.
  .fc .fc-toolbar.fc-header-toolbar {
    margin-bottom: 0.75rem;
    padding: 0.25rem 0;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .fc .fc-toolbar-title {
    font-size: 1.3rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    margin: 0 0.5rem;
    text-transform: capitalize;
  }
  .fc .fc-button {
    padding: 0.32rem 0.7rem;
    font-size: 0.78rem;
    font-weight: 600;
    border-radius: 6px;
    border: 1px solid transparent;
    background-color: rgba(0, 123, 255, 0.10);
    color: #007BFF;
    box-shadow: none !important;
    text-transform: capitalize;
    transition: background-color .15s ease, color .15s ease, transform .05s ease;
  }
  .fc .fc-button:hover {
    background-color: rgba(0, 123, 255, 0.20);
    color: #0062cc;
  }
  .fc .fc-button:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25) !important;
  }
  .fc .fc-button:active { transform: translateY(1px); }
  .fc .fc-button-primary:not(:disabled).fc-button-active,
  .fc .fc-button-primary:not(:disabled):active {
    background-color: #007BFF;
    color: #ffffff;
    border-color: #007BFF;
  }
  // Segment the view-switcher group on the right into a single pill.
  .fc .fc-button-group {
    background: rgba(0, 123, 255, 0.08);
    border-radius: 8px;
    padding: 2px;
    gap: 2px;
  }
  .fc .fc-button-group > .fc-button {
    border-radius: 6px;
    background: transparent;
    color: #6e6b7b;
  }
  .fc .fc-button-group > .fc-button.fc-button-active {
    background: #ffffff;
    color: #007BFF;
    box-shadow: 0 1px 3px rgba(0,0,0,0.08) !important;
  }
  // Prev/next: small icon-only ghost buttons.
  .fc .fc-prev-button,
  .fc .fc-next-button {
    background: transparent;
    color: #6e6b7b;
    width: 32px;
    padding: 0;
  }
  .fc .fc-prev-button:hover,
  .fc .fc-next-button:hover {
    background: rgba(0, 123, 255, 0.12);
    color: #007BFF;
  }

  // ----- Weekday header row -----
  // Uppercase, tracked, muted — looks like a modern admin dashboard.
  .fc .fc-col-header-cell {
    padding: 8px 4px;
    background-color: rgba(0, 123, 255, 0.04);
  }
  .fc .fc-col-header-cell-cushion {
    color: #6e6b7b;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    text-decoration: none !important;
    padding: 4px 6px;
  }

  // ----- Day cells -----
  // Soft hover, gentle weekend tint, generous but compact padding.
  .fc .fc-daygrid-day {
    transition: background-color .15s ease;
  }
  .fc .fc-daygrid-day:hover {
    background-color: rgba(0, 123, 255, 0.05);
  }
  .fc .fc-day-sat,
  .fc .fc-day-sun {
    background-color: rgba(0, 0, 0, 0.015);
  }
  .fc .fc-day-other {
    background-color: rgba(0, 0, 0, 0.02);
  }
  .fc .fc-day-other .fc-daygrid-day-number {
    opacity: 0.4;
  }
  .fc .fc-daygrid-day-top {
    padding: 4px 6px 0 6px;
  }
  .fc .fc-daygrid-day-number {
    font-size: 0.8rem;
    font-weight: 600;
    color: #5e5873;
    padding: 2px 6px;
    line-height: 1.1;
    text-decoration: none !important;
  }
  // Scrollable day cell. We show every event (dayMaxEvents: false). If the
  // events stayed in normal flow they'd stretch the table row (expandRows
  // can grow a row but never shrinks it back below its content). So we pull
  // the events list OUT of flow with absolute positioning — the same trick
  // FullCalendar uses in its "balanced" mode. The cell then sizes to just
  // the date number, expandRows distributes the height evenly, and the
  // events scroll inside the fixed area below the date.
  .fc .fc-daygrid-day-frame {
    position: relative;
    height: 100%;
    // Fill the viewport: the calendar wrapper is ~calc(100vh - 180px) tall
    // and a month shows up to 6 week rows (plus the header). Sizing each
    // cell to roughly that share makes the cells fill the available space
    // (so as many events as fit are shown) instead of sitting at a small
    // floor. expandRows can still stretch taller; this is the minimum.
    min-height: calc((100vh - 280px) / 6);
    overflow: hidden;
  }
  // !important is required: FC's own `.fc .fc-daygrid-body-unbalanced
  // .fc-daygrid-day-events { position: relative }` rule (3 classes) is more
  // specific than ours, so without it our absolute positioning is ignored
  // and the events fall back into flow and stretch the row.
  .fc .fc-daygrid-day-events {
    position: absolute !important;
    top: 2.6em;   // clear the date-number row with a little breathing room
    left: 0;
    right: 0;
    bottom: 2px;
    margin-top: 0;
    padding-right: 2px;   // keep event pills off the scrollbar
    overflow-y: auto;
    overflow-x: hidden;

    // ----- Custom scrollbar -----
    // Floating pill thumb: full radius, transparent gutter via a padding-box
    // border so it looks inset, fades in on hover, and the track stays
    // invisible so the cell reads clean when nothing's scrolling.
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;          // hidden until hover (Firefox)
    transition: scrollbar-color .2s ease;
    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: transparent;                    // hidden until hover
      border-radius: 8px;
      border: 2px solid transparent;                    // inset gutter
      background-clip: padding-box;
      transition: background-color .2s ease;
    }
    // Reveal the thumb when the user hovers the cell (or interacts with it).
    &:hover {
      scrollbar-color: rgba(0, 123, 255, 0.35) transparent;
      &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 123, 255, 0.35);
      }
    }
    &::-webkit-scrollbar-thumb:hover {
      background-color: rgba(0, 123, 255, 0.6);
    }
  }
  // No more-link anymore — drop the spacer FC reserves for it.
  .fc .fc-daygrid-day-bottom {
    display: none;
  }

  // ----- Events -----
  // Pill-style events: rounded corners, subtle elevation on hover. Bumped
  // padding + font + line-height so titles are actually legible (previous
  // version was too cramped). min-height locks a comfortable row height
  // even when the title is one short word.
  .fc-daygrid-event {
    padding: 4px 8px;
    font-size: 0.8rem;
    line-height: 1.35;
    min-height: 22px;
    margin: 3px 5px 0 5px;
    border-radius: 6px;
    border: none !important;
    box-shadow: 0 1px 2px rgba(0,0,0,0.04);
    transition: transform .1s ease, box-shadow .15s ease;
    cursor: pointer;
    display: flex;
    align-items: center;
  }
  .fc-daygrid-event:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0,0,0,0.10);
  }
  .fc-daygrid-event .fc-event-time {
    font-weight: 700;
    opacity: 0.85;
    margin-right: 5px;
    font-size: 0.75rem;
  }
  .fc-daygrid-event .fc-event-title {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // ----- Time-grid (week / day) views -----
  // Quieter grid lines, accented "now" indicator.
  .fc .fc-timegrid-slot {
    height: 2.2em;
  }
  .fc .fc-timegrid-slot-label-cushion,
  .fc .fc-timegrid-axis-cushion {
    font-size: 0.72rem;
    color: #6e6b7b;
    font-weight: 500;
  }
  .fc .fc-timegrid-now-indicator-line {
    border-color: #ea5455;
    border-width: 2px;
  }
  .fc .fc-timegrid-now-indicator-arrow {
    border-color: #ea5455;
  }

  // ----- List view -----
  .fc .fc-list {
    border-radius: 10px;
    border-color: rgba(34, 41, 47, 0.08);
  }
  .fc .fc-list-day-cushion {
    background-color: rgba(0, 123, 255, 0.06) !important;
    font-weight: 700;
  }
  .fc .fc-list-event:hover td {
    background-color: rgba(0, 123, 255, 0.08);
  }

  // Today highlight (light mode) — VERY loud. Tinted cell background, a
  // thick 2px indigo border on all four sides of the cell, plus a large
  // filled indigo circle around the day number with a glow. Impossible
  // to miss even with the cell packed with events.
  .fc .fc-day-today {
    background-color: rgba(0, 123, 255, 0.18) !important;
    box-shadow: inset 0 0 0 2px #007BFF !important;
    position: relative;
  }
  // Small "TODAY" badge in the top-RIGHT corner of the cell so the day
  // number stays on the left, aligned with every other day in the grid.
  .fc .fc-day-today .fc-daygrid-day-top::after {
    content: 'TODAY';
    position: absolute;
    top: 4px;
    right: 6px;
    font-size: 0.6rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    color: #007BFF;
    background: #ffffff;
    padding: 1px 5px;
    border-radius: 4px;
    box-shadow: 0 0 0 1px #007BFF;
    z-index: 1;
    pointer-events: none;
  }
  .fc .fc-day-today .fc-daygrid-day-number {
    background-color: #007BFF;
    color: #ffffff !important;
    border-radius: 999px;
    font-weight: 700;
    font-size: 0.85rem;
    min-width: 26px;
    height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.30),
                0 2px 6px rgba(0, 123, 255, 0.45);
    margin: 2px;
  }
  // Make the "today" column header also stand out in the week/day views.
  .fc .fc-col-header-cell.fc-day-today {
    background-color: #007BFF !important;
  }
  .fc .fc-col-header-cell.fc-day-today .fc-col-header-cell-cushion {
    color: #ffffff !important;
    font-weight: 800;
  }
}

.calendar{


  .fc-daygrid-event {
    &.bg-light-info .fc-event-main{
      color: #1b2337 !important;
    }
    &.fc-dot-success .fc-daygrid-event-dot {
      border-color: #28a745;
    }

    &.fc-dot-danger .fc-daygrid-event-dot {
      border-color: #dc3545;
    }

    &.fc-dot-dark .fc-daygrid-event-dot {
      border-color: #4b4b4b;
    }

    &.fc-dot-primary .fc-daygrid-event-dot {
      border-color: #007bff;
    }

    &.fc-event-cancelled {
      opacity: 0.8;
      text-decoration-line: line-through !important;
      .fc-event-title{
        text-decoration-line: line-through !important;
      }
    }

  }
}

// =====================================================================
// Event-details modal (brand new UI; replaces the old b-popover loop).
// Lives outside .calendar because b-modal is teleported to <body>.
// =====================================================================
.cal-event-modal {
  .cal-event-modal__content {
    border-radius: 14px;
    overflow: hidden;
    border: none;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  }

  // Hero strip with a soft gradient. The variant modifier swaps the
  // gradient stops so the header colour matches the event's badge.
  .cal-event-modal__hero {
    position: relative;
    padding: 1.5rem 1.5rem 1.25rem 1.5rem;
    color: #ffffff;
    background: linear-gradient(135deg, #007BFF 0%, #4ba6ff 100%);
  }
  .cal-event-modal__hero--success {
    background: linear-gradient(135deg, #28c76f 0%, #4ddc8b 100%);
  }
  .cal-event-modal__hero--danger {
    background: linear-gradient(135deg, #ea5455 0%, #f08182 100%);
  }
  .cal-event-modal__hero--dark {
    background: linear-gradient(135deg, #4b4b4b 0%, #6e6b7b 100%);
  }
  .cal-event-modal__hero--primary {
    background: linear-gradient(135deg, #007BFF 0%, #4ba6ff 100%);
  }

  .cal-event-modal__title h3 {
    color: #ffffff;
    font-weight: 700;
    font-size: 1.4rem;
    margin: 0.25rem 0 0 0;
    word-break: break-word;
  }
  .cal-event-modal__date {
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    opacity: 0.92;
    text-transform: uppercase;
  }

  .cal-event-modal__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .cal-event-modal__pill {
    display: inline-flex;
    align-items: center;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 3px 10px;
    border-radius: 999px;
    background: rgba(255,255,255,0.22);
    color: #ffffff;
    backdrop-filter: blur(2px);
  }
  // Type-color modifiers (match the calendar type palette).
  .cal-event-modal__pill--primary   { background: rgba(255,255,255,0.25); }
  .cal-event-modal__pill--success   { background: rgba(255,255,255,0.25); }
  .cal-event-modal__pill--danger    { background: rgba(255,255,255,0.25); }
  .cal-event-modal__pill--warning   { background: rgba(255,255,255,0.25); }
  .cal-event-modal__pill--info      { background: rgba(255,255,255,0.25); }
  .cal-event-modal__pill--secondary { background: rgba(0,0,0,0.25); }

  // Round close button in the hero corner.
  .cal-event-modal__close {
    color: #ffffff !important;
    background: rgba(255,255,255,0.15) !important;
    width: 32px;
    height: 32px;
    padding: 0 !important;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none !important;
  }
  .cal-event-modal__close:hover {
    background: rgba(255,255,255,0.30) !important;
  }

  // Body wraps the existing calendarEventBasic component. Padding here
  // (not on the body via b-modal) so the hero can run edge-to-edge.
  .cal-event-modal__body {
    padding: 1.25rem 1.5rem 0.5rem 1.5rem;
    max-height: 60vh;
    overflow-y: auto;
  }

  // Footer action bar — divider above, right-aligned buttons.
  .cal-event-modal__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid rgba(34, 41, 47, 0.08);
    background: rgba(0, 0, 0, 0.015);
  }
}

// Dark-mode delta for the modal.
.dark-layout .cal-event-modal {
  .cal-event-modal__content {
    background: #283046;
    color: #d0d2d6;
  }
  .cal-event-modal__footer {
    background: rgba(255, 255, 255, 0.02);
    border-top-color: rgba(255, 255, 255, 0.08);
  }
}
</style>
